<script>
import { useUserStore } from "../../../stores/user";
import { useOrganizationStore } from "../../../stores/organizations";
import { useRoute } from "vue-router";
import Drawer from "../../components/Drawer.vue";
import Paginator from "../../components/Pagination.vue";

export default {
  components: { Drawer, Paginator },
  setup() {
    const userStore = useUserStore();
    const organizationStore = useOrganizationStore();
    const route = useRoute();
    return { userStore, organizationStore, route };
  },
  data() {
    return {
      householdName: null,
      householdRole: null,
      householdGiving: null,
      householdMembers: [],
      householdAddress: null,
      householdId: null,
      allInvites: [],
      pendingInvites: [],
      inviteStatusFilter: "all",
      inviteEmail: "",
      inviteRole: "Spouse",
      editNameValue: "",
      editAddress: { street: "", city: "", state: "", postal_code: "", country: "" },
      drawerMode: null,
      selectedYear: new Date().getFullYear(),
      availableYears: [],
      reportLoading: false,
      yearLoading: false,
      reportError: null,
      invitePage: 1,
      invitesPerPage: 5,
    };
  },
  computed: {
    organizationId() {
      return this.route.params.org;
    },
    canInvite() {
      return ["Head of Household", "Spouse"].includes(this.householdRole);
    },
    filteredInvites() {
      if (this.inviteStatusFilter === "all") return this.allInvites;
      return this.allInvites.filter(i => i.status === this.inviteStatusFilter);
    },
    paginatedInvites() {
      const start = (this.invitePage - 1) * this.invitesPerPage;
      return this.filteredInvites.slice(start, start + this.invitesPerPage);
    },
    inviteTotalPages() {
      return Math.ceil(this.filteredInvites.length / this.invitesPerPage);
    },
  },
  mounted() {
    this.loadHousehold();
  },
  methods: {
    loadHousehold() {
      const household = this.userStore.user?.household;
      if (!household) return;

      this.householdName = household.name;
      this.householdRole = household.role;
      this.householdId = household.id;

      // Fetch invites
      fetch(`/organizations/${this.organizationId}/household_invites`, {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
      }).then(res => res.json()).then(data => {
        this.allInvites = data.invites || [];
        this.pendingInvites = this.allInvites.filter(i => i.status === "pending");
      }).catch(() => {});

      // Fetch full household details
      fetch(`/organizations/${this.organizationId}/households/${household.id}?year=${this.selectedYear}`, {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
      }).then(res => res.json()).then(data => {
        this.householdMembers = data.members || [];
        const r = data.record || {};
        if (r.name) this.householdName = r.name;
        if (data.total_giving !== undefined) this.householdGiving = data.total_giving;
        if (data.available_years) this.availableYears = data.available_years;
        if (r.address_street) {
          this.householdAddress = [r.address_street, r.address_city, r.address_state, r.address_postal_code].filter(Boolean).join(", ");
        } else {
          this.householdAddress = null;
        }
        this.editAddress = {
          street: r.address_street || "",
          city: r.address_city || "",
          state: r.address_state || "",
          postal_code: r.address_postal_code || "",
          country: r.address_country || ""
        };
      }).catch(() => {}).finally(() => { this.yearLoading = false; });
    },

    changeYear() {
      this.yearLoading = true;
      this.loadHousehold();
    },

    downloadHouseholdReport() {
      this.reportLoading = true;
      this.reportError = null;
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      // 1. Trigger report generation
      fetch(`/organizations/${this.organizationId}/households/${this.householdId}/report?year=${this.selectedYear}`, {
        method: "POST",
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken }
      })
        // Error Handling
        .then(res => {
          if (!res.ok) return res.json().then(data => { throw new Error(data.error || "Failed to generate report"); });
          return res.json();
        })
        .then(data => {
          const reportId = data.report?.id;
          if (!reportId) throw new Error("No report ID returned");
          return this.pollReportStatus(reportId);
        })
        .then(reportId => {
          this.downloadReport(reportId);
          this.reportLoading = false;
        })
        .catch(err => {
          this.reportError = err.message || "Failed to generate report. Please try again.";
          this.reportLoading = false;
        });
    },

    pollReportStatus(reportId, attempt = 1, maxAttempts = 20) {
      return new Promise((resolve, reject) => {
        const check = () => {
          fetch(`/organizations/${this.organizationId}/reports/${reportId}`, {
            headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
          })
            .then(res => res.json())
            .then(data => {
              const status = data.record?.status || data.data?.status;
              if (status === "COMPLETED") {
                resolve(reportId);
              } else if (status === "FAILED") {
                reject(new Error("Report generation failed"));
              } else if (attempt >= maxAttempts) {
                reject(new Error("Report generation timed out"));
              } else {
                setTimeout(() => {
                  attempt++;
                  check();
                }, 2000);
              }
            })
            .catch(reject);
        };
        check();
      });
    },

    downloadReport(reportId) {
      const link = document.createElement("a");
      link.href = `/organizations/${this.organizationId}/reports/${reportId}/download_report`;
      link.download = `household_tax_year_${this.selectedYear}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    openHouseholdDrawer(mode) {
      this.drawerMode = mode;
      if (mode === "edit") this.editNameValue = this.householdName;
      this.$refs.householdDrawer.show();
    },

    saveHousehold() {
      if (!this.editNameValue.trim()) return alert("Name cannot be empty");
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      fetch(`/organizations/${this.organizationId}/households/${this.householdId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({ household: { name: this.editNameValue, address_street: this.editAddress.street, address_city: this.editAddress.city, address_state: this.editAddress.state, address_postal_code: this.editAddress.postal_code, address_country: this.editAddress.country } })
      }).then(res => res.json()).then(data => {
        if (data.errors) {
          alert(Object.values(data.errors).flat().join("\n"));
        } else {
          this.$refs.householdDrawer.hide();
          this.loadHousehold();
        }
      });
    },

    sendInvite() {
      if (!this.inviteEmail) return alert("Email is required");
      // Mental note to self - this token is necessary (keep forgetting)
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      fetch(`/organizations/${this.organizationId}/household_invites`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({ email: this.inviteEmail, role: this.inviteRole })
      }).then(res => res.json()).then(data => {
        if (data.errors) {
          alert(Object.values(data.errors).flat().join("\n"));
        } else {
          alert(`An email invitation has been sent to ${this.inviteEmail}`);
          this.inviteEmail = "";
          this.$refs.householdDrawer.hide();
          this.loadHousehold();
        }
      });
    },

    cancelInvite(uuid) {
      if (!confirm("Cancel this invitation?")) return;
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      fetch(`/organizations/${this.organizationId}/household_invites/${uuid}`, {
        method: "DELETE",
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken }
      }).then(() => this.loadHousehold());
    },

    canKick(member) {
      if (member.user_id == this.userStore.user?.id) return false;
      if (this.householdRole === "Head of Household") return true;
      if (this.householdRole === "Spouse" && member.role !== "Head of Household") return true;
      return false;
    },
    canPromote(member) {
      if (this.householdRole !== "Head of Household") return false;
      if (member.user_id == this.userStore.user?.id) return false;
      if (member.role === "Head of Household") return false;
      return true;
    },
    kickMember(member) {
      if (!confirm(`Remove ${member.name} from this household? A new household will be created for them.`)) return;
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      fetch(`/organizations/${this.organizationId}/household_members/${member.id}`, {
        method: "DELETE",
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken }
      }).then(res => {
        if (res.ok) this.loadHousehold();
        else res.json().then(data => alert(Object.values(data.errors || {}).flat().join("\n")));
      });
    },

    leaveHousehold() {
      if (!confirm("You'll be removed from this household. A new household will be created for you.")) return;
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      fetch(`/organizations/${this.organizationId}/households/leave`, {
        method: "POST",
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken }
      }).then(res => {
        if (res.ok) {
          this.userStore.loadData(this.organizationId).then(() => this.loadHousehold());
        } else {
          res.json().then(data => alert(Object.values(data.errors || {}).flat().join("\n")));
        }
      });
    },
    promoteMember(member) {
      if (!confirm(`Transfer Head of Household to ${member.name}? You'll become a Spouse.`)) return;
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      fetch(`/organizations/${this.organizationId}/household_members/${member.id}/promote`, {
        method: "POST",
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken }
      }).then(res => {
        if (res.ok) this.loadHousehold();
        else res.json().then(data => alert(Object.values(data.errors || {}).flat().join("\n")));
      });
    },
  }
};
</script>

<template>
  <div class="w-full">
    <div class="bg-white rounded-lg border border-slate-200 p-6 shadow">

      <!-- Household Name -->
      <div class="flex items-center gap-3 mb-4">
        <h2 class="text-xl font-bold">{{ householdName }}</h2>
        <button v-if="canInvite" class="text-xs btn-brand" @click="openHouseholdDrawer('edit')">Edit</button>
      </div>

      <!-- Address -->
      <div class="mb-4">
        <h3 class="text-sm font-semibold text-gray-700 mb-2">Address</h3>
        <p v-if="householdAddress" class="text-sm text-gray-600">{{ householdAddress }}</p>
        <p v-else class="text-sm text-gray-400 italic">No address set</p>
      </div>

      <!-- Giving Total + Year Selector (HOH/Spouse only) -->
      <div v-if="canInvite" class="mb-6">
        <div class="flex items-center gap-3 mb-2">
          <p class="text-sm text-gray-600">Household giving:</p>
          <select v-model="selectedYear" class="text-sm border border-gray-300 rounded px-2 py-1" @change="changeYear">
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
          </select>
        </div>
        <p v-if="!yearLoading" class="text-lg font-semibold text-green-600">${{ (householdGiving / 100).toLocaleString() }}</p>
        <p v-else class="text-sm font-semibold text-gray-500 animate-pulse">Loading...</p>
      </div>

      <!-- Members -->
      <div class="mb-6">
        <h3 class="text-sm font-semibold text-gray-700 mb-3">Members</h3>
        <div class="space-y-2">
          <div v-for="member in householdMembers" :key="member.id" class="flex items-center justify-between border rounded-lg p-3">
            <div>
              <p class="font-medium text-sm">{{ member.name }}</p>
              <p class="text-xs text-gray-500">{{ member.role }}</p>
              <p v-if="!member.has_donor_in_org" class="text-xs text-orange-500">Hasn't made a donation to this organization yet</p>
              <p v-else-if="canInvite && member.giving_total !== undefined" class="text-xs text-green-600">${{ (member.giving_total / 100).toLocaleString() }} in
                {{selectedYear}}</p>
            </div>
            <div class="flex gap-2">
              <button
                v-if="canPromote(member)"
                class="text-xs btn-brand"
                @click="promoteMember(member)"
              >Make HOH</button>
              <button
                v-if="canKick(member)"
                class="btn border border-red-700 hover:bg-red-700 text-xs text-red-700 hover:text-white"
                @click="kickMember(member)"
              >Remove</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-3 mb-6">
        <button v-if="canInvite" type="button" class="btn-brand" @click="openHouseholdDrawer('invite')">
          ✉ Invite Member
        </button>
        <button
          v-if="householdMembers.length > 1"
          type="button"
          class="btn border border-red-700 hover:bg-red-700 text-xs text-red-700 hover:text-white"
          @click="leaveHousehold"
        >
          Leave Household
        </button>
      </div>

    </div>

    <!-- Tax Year Report (HOH/Spouse only) -->
    <div v-if="canInvite" class="bg-white rounded-lg border border-slate-200 p-6 mt-6 shadow">
      <h3 class="text-lg font-semibold text-gray-800 mb-3">Tax Year Report</h3>
      <p class="text-sm text-gray-500 mb-4">Download a PDF summary of your household's giving for the selected tax year.</p>

      <div class="flex items-center gap-4">
        <div class="flex items-center gap-2">
          <label class="text-sm text-gray-600">Year:</label>
          <select v-model="selectedYear" class="text-sm border border-gray-300 rounded px-2 py-1" @change="changeYear">
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
          </select>
        </div>

        <button
          type="button"
          class="btn-brand"
          :disabled="reportLoading"
          @click="downloadHouseholdReport"
        >
          {{ reportLoading ? 'Generating...' : '📄 Download Tax Year PDF' }}
        </button>
      </div>
      <p v-if="reportError" class="text-xs text-red-500 mt-2">{{ reportError }}</p>
    </div>

    <!-- Invites -->
    <div v-if="allInvites.length" class="bg-white rounded-lg border border-slate-200 p-6 mt-6 shadow">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-semibold text-gray-700">Invites</h3>
        <select v-model="inviteStatusFilter" class="text-xs border border-gray-300 rounded px-2 py-1" @change="invitePage = 1">
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="denied">Denied</option>
          <option value="expired">Expired</option>
        </select>
      </div>
      <div v-for="invite in paginatedInvites" :key="invite.uuid" class="flex items-center justify-between border rounded-lg p-3 mb-2">
        <div>
          <p class="text-sm">{{ invite.email }}</p>
          <p class="text-xs text-gray-400">{{ invite.role }} · <span class="capitalize">{{ invite.status }}</span> · Sent {{ new Date(invite.created_at).toLocaleDateString() }}</p>
        </div>
        <button v-if="canInvite && invite.status === 'pending'" class="btn border border-red-700 hover:bg-red-700 text-xs text-red-700 hover:text-white" @click="cancelInvite(invite.uuid)">Cancel</button>
      </div>
      <p v-if="filteredInvites.length === 0" class="text-xs text-gray-400 italic">No {{ inviteStatusFilter }} invites</p>

      <!-- Pagination -->
      <div class="mt-3">
        <paginator
          :page="invitePage"
          :total-pages="inviteTotalPages"
          :records-showing="paginatedInvites.length"
          :records-per-page="invitesPerPage"
          :total-records="filteredInvites.length"
          @next="invitePage++"
          @prev="invitePage--"
          @move-to-page="invitePage = $event"
        />
      </div>
    </div>
  </div>

  <!-- Household Edit Drawer -->
  <drawer ref="householdDrawer" size="large">
    <div class="p-6 space-y-6 overflow-y-auto h-screen">
      <!-- Edit Name & Address -->
      <template v-if="drawerMode === 'edit'">
        <h2 class="text-xl font-bold">Edit Household</h2>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Household Name</label>
            <input v-model="editNameValue" class="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <hr>
          <h3 class="text-sm font-semibold text-gray-700">Mailing Address</h3>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Street</label>
            <input v-model="editAddress.street" placeholder="Street" class="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input v-model="editAddress.city" placeholder="City" class="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
              <input v-model="editAddress.state" placeholder="State/Province" class="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Postal/ZIP Code</label>
              <input v-model="editAddress.postal_code" placeholder="Postal/ZIP Code" class="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input v-model="editAddress.country" placeholder="Country" class="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
          </div>
          <div class="flex gap-3 pt-2">
            <button class="btn-brand" @click="saveHousehold">Save</button>
            <button class="btn-ghost" @click="$refs.householdDrawer.hide()">Cancel</button>
          </div>
        </div>
      </template>

      <!-- Invite Member -->
      <template v-else-if="drawerMode === 'invite'">
        <h2 class="text-xl font-bold">Invite Member</h2>
        <div class="space-y-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input v-model="inviteEmail" type="email" placeholder="name@example.com" class="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select v-model="inviteRole" class="w-full border border-gray-300 rounded-lg px-3 py-2">
              <option value="Spouse">Spouse</option>
              <option value="Child">Child</option>
              <option value="Dependent">Dependent</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div class="flex gap-3">
            <button class="btn-brand" @click="sendInvite">Send Invitation</button>
            <button class="btn-ghost" @click="$refs.householdDrawer.hide()">Cancel</button>
          </div>
        </div>
      </template>
    </div>
  </drawer>
</template>
